
/**
 * Debug utilities for Twilio integration
 */

export interface TwilioDebugInfo {
  hasCredentials: boolean;
  credentialsValid: boolean;
  configIssues: string[];
  environmentVars: {
    sid: string;
    auth: string;
    from: string;
  };
}

export interface TwilioCredentialTestResult {
  success: boolean;
  accountInfo?: {
    sid: string;
    friendlyName: string;
    status: string;
  };
  error?: string;
  errorCode?: string;
}

export const debugTwilioConfig = (): TwilioDebugInfo => {
  const sid = import.meta.env.VITE_TWILIO_SID?.trim();
  const auth = import.meta.env.VITE_TWILIO_AUTH?.trim();
  const from = import.meta.env.VITE_TWILIO_FROM?.trim();
  
  const issues: string[] = [];
  
  // Check if credentials exist
  if (!sid) issues.push('VITE_TWILIO_SID is missing');
  if (!auth) issues.push('VITE_TWILIO_AUTH is missing');  
  if (!from) issues.push('VITE_TWILIO_FROM is missing');
  
  // Enhanced validation for production
  if (sid && !sid.startsWith('AC')) {
    issues.push('VITE_TWILIO_SID must start with "AC" (currently: ' + (sid.substring(0, 5) || 'empty') + '...)');
  }
  
  if (sid && sid.length !== 34) {
    issues.push(`VITE_TWILIO_SID must be exactly 34 characters (currently: ${sid.length})`);
  }
  
  if (auth && auth.length !== 32) {
    issues.push(`VITE_TWILIO_AUTH must be exactly 32 characters (currently: ${auth.length})`);
  }
  
  if (from && !from.match(/^\+?[1-9]\d{10,14}$/)) {
    issues.push('VITE_TWILIO_FROM must be in E.164 format (e.g., +15551234567)');
  }
  
  // Additional production-specific checks
  if (sid && sid.includes(' ')) {
    issues.push('VITE_TWILIO_SID contains spaces - remove all spaces');
  }
  
  if (auth && auth.includes(' ')) {
    issues.push('VITE_TWILIO_AUTH contains spaces - remove all spaces');
  }
  
  // Enhanced logging for production debugging
  const envInfo = {
    mode: import.meta.env.MODE,
    prod: import.meta.env.PROD,
    baseUrl: import.meta.env.BASE_URL
  };
  
  console.log('🔍 Enhanced Twilio Debug Info:', {
    environment: envInfo,
    sid: sid ? `${sid.substring(0, 10)}...` : 'MISSING',
    auth: auth ? `${auth.substring(0, 8)}...` : 'MISSING',
    from: from || 'MISSING',
    sidLength: sid?.length || 0,
    authLength: auth?.length || 0,
    issues,
    timestamp: new Date().toISOString()
  });
  
  return {
    hasCredentials: !!(sid && auth && from),
    credentialsValid: issues.length === 0,
    configIssues: issues,
    environmentVars: {
      sid: sid || '',
      auth: auth || '',
      from: from || ''
    }
  };
};

/**
 * Enhanced Twilio credentials test with better production debugging
 */
export const testTwilioCredentials = async (): Promise<TwilioCredentialTestResult> => {
  const sid = import.meta.env.VITE_TWILIO_SID?.trim();
  const auth = import.meta.env.VITE_TWILIO_AUTH?.trim();
  
  console.log('🔐 Starting enhanced credential test...');
  console.log('🔐 Environment context:', {
    mode: import.meta.env.MODE,
    prod: import.meta.env.PROD,
    timestamp: new Date().toISOString()
  });
  
  if (!sid || !auth) {
    return {
      success: false,
      error: 'Missing Twilio credentials - check VITE_TWILIO_SID and VITE_TWILIO_AUTH',
      errorCode: 'MISSING_CREDENTIALS'
    };
  }
  
  // Pre-validate credentials format before making API call
  if (!sid.startsWith('AC') || sid.length !== 34) {
    return {
      success: false,
      error: `Invalid Account SID format. Expected: AC followed by 32 characters (total 34). Got: ${sid.length} characters starting with "${sid.substring(0, 5)}"`,
      errorCode: 'INVALID_SID_FORMAT'
    };
  }
  
  if (auth.length !== 32) {
    return {
      success: false,
      error: `Invalid Auth Token length. Expected: 32 characters. Got: ${auth.length} characters. Please copy the complete Auth Token from Twilio Console.`,
      errorCode: 'INVALID_AUTH_LENGTH'
    };
  }
  
  try {
    console.log('🔐 Making API call to validate credentials...');
    const credentials = btoa(`${sid}:${auth}`);
    const apiUrl = `https://api.twilio.com/2010-04-01/Accounts/${sid}.json`;
    
    console.log('🔐 API URL:', apiUrl);
    console.log('🔐 Request headers prepared');
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/json',
      },
    });
    
    console.log('📡 Twilio API Response Status:', response.status);
    console.log('📡 Response Headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { message: errorText || `HTTP Error: ${response.status}` };
      }
      
      console.error('🔴 Twilio API Error Response:', errorData);
      console.error('🔴 Full response text:', errorText);
      
      let errorCode = 'UNKNOWN_ERROR';
      let detailedError = errorData.message || `Authentication failed: ${response.status}`;
      
      if (response.status === 401) {
        errorCode = 'INVALID_CREDENTIALS';
        detailedError = `Authentication failed (401): Invalid Account SID or Auth Token. Please verify your credentials in the Twilio Console.
        
Debugging info:
- SID starts with: ${sid?.substring(0, 5) || 'undefined'}...
- SID length: ${sid?.length || 0}
- Auth Token length: ${auth.length}
- Environment: ${import.meta.env.MODE}`;
        
        // Provide specific guidance for common credential issues
        if (errorData.code === 20003) {
          detailedError += '\n\nError 20003: Authentication failed - double-check both SID and Auth Token from the same Twilio project.';
        }
      } else if (response.status === 403) {
        errorCode = 'ACCESS_FORBIDDEN';
        detailedError = 'Access forbidden: Your Twilio account may be suspended or have insufficient permissions.';
      }
      
      return {
        success: false,
        error: detailedError,
        errorCode
      };
    }
    
    const accountData = await response.json();
    console.log('✅ Twilio credentials validated successfully');
    console.log('✅ Account data received:', {
      sid: accountData.sid,
      friendlyName: accountData.friendly_name,
      status: accountData.status
    });
    
    return {
      success: true,
      accountInfo: {
        sid: accountData.sid,
        friendlyName: accountData.friendly_name,
        status: accountData.status
      }
    };
  } catch (error: any) {
    console.error('🔴 Network error during credential test:', error);
    console.error('🔴 Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    
    return {
      success: false,
      error: `Network error: ${error.message}. Check your internet connection and try again.`,
      errorCode: 'NETWORK_ERROR'
    };
  }
};

export const logTwilioApiCall = (endpoint: string, method: string, params?: any) => {
  console.log('📞 Twilio API Call:', {
    endpoint,
    method,
    timestamp: new Date().toISOString(),
    params: params ? JSON.stringify(params) : 'none'
  });
};

export const logTwilioError = (error: any, context: string) => {
  console.error('❌ Twilio Error:', {
    context,
    error: error.message || error,
    stack: error.stack,
    timestamp: new Date().toISOString()
  });
};

export const logTwilioSuccess = (result: any, context: string) => {
  console.log('✅ Twilio Success:', {
    context,
    result,
    timestamp: new Date().toISOString()
  });
};
